import React, { useState, useRef } from "react";
import {
  Button,
  Typography as MuiTypography,
  Box,
} from "../../../common/utils/mui-imports";
import { DocumentUpload } from "iconsax-react";
import { HPRFileUploaderWithNavigationProps } from "./HPRFileUploaderWithNavigation.types";

import { UploadFileDTO } from "../../../common/types/common.dto.types";
import { useAppDispatch, useAppSelector } from "../../../core/hooks";
import { UploadFileSetting } from "../../../views/layouts/main-layout/MainLayout.types";
import { EnumPopupType } from "../../Popups/Popup.enum";
import { PopupProps } from "../../Popups/PopupProps.types";
import { openPopup } from "../../Popups/Popup.reducer";
import {
  EnumDataTransferStatus,
  EnumFileType,
} from "../../../common/enums/common.enums";

const HPRFileUploaderWithNavigation = (
  props: HPRFileUploaderWithNavigationProps
) => {
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);

  const mainLayoutState = useAppSelector((state) => state.mainLayoutReducer);

  // reducer manage fucntions
  const dispatch = useAppDispatch();

  const uploadFileTypes = [
    {
      FileType: EnumFileType.Excel,
      UploadFolder: "",
      AllowedFileTypes: ".xls,.xlsx",
      AllowedMaxFileSize: 1048576,
    } as UploadFileSetting,
  ];

  const uploadFileSettings: Array<UploadFileSetting> =
    //mainLayoutState.AppConfiguration.UploadFileSettings
    uploadFileTypes?.filter((x) => props.FileType.includes(x.FileType));

  const getFileType = (file: File): EnumFileType => {
    let fileType = EnumFileType.Default;
    for (let x = 0; x < uploadFileSettings?.length; x++) {
      let requiredTypeArray =
        uploadFileSettings[x]?.AllowedFileTypes?.split(",");

      if (requiredTypeArray != null && requiredTypeArray.length > 0) {
        for (let y = 0; y < requiredTypeArray.length; y++) {
          if (
            requiredTypeArray[y].includes(
              file.name.split(".").pop().split("/").pop().toLowerCase()
            ) ||
            (file.name != null && file.name.endsWith(requiredTypeArray[y]))
          ) {
            fileType = uploadFileSettings[x].FileType;

            return fileType;
          }
        }
      }
    }

    return fileType;
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files) return;

    const files = event.target.files;
    if (files) {
      processFiles(files);
    }

    //clear event after upload
    event.target.value = null;
  };

  // File upload handlers
  const handleDrag = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const files = event.dataTransfer.files;
    if (files) {
      processFiles(files);
    }

    //clear event after upload
    event.target = null;
  };

  const processFiles = (files: FileList) => {
    let fileType = EnumFileType.Default;

    let uploadedFileList: Array<UploadFileDTO> = props.UploadedFileList
      ? [...props.UploadedFileList]
      : ([] as Array<UploadFileDTO>);
    let errorMessages: Array<string> = [] as Array<string>;

    const getMaxAllowedSize = (): string => {
      let requiredFileConfig = uploadFileSettings.find(
        (x) => x.FileType == fileType
      );
      let allowedSize: number = requiredFileConfig?.AllowedMaxFileSize;

      let size = "";
      if (allowedSize < 1024) {
        size = allowedSize + "B";
      } else if (allowedSize < 1024 * 1024) {
        size = +(allowedSize / 1024).toFixed(2) + "KB";
      } else {
        size = +(allowedSize / (1024 * 1024)).toFixed(2) + "MB";
      }

      return size;
    };

    for (const _file of files) {
      let file: File = _file;
      const filename: string = file.name;
      let size = "";

      // ==============================|| START FILE VALIDATION ||============================== //

      if (file.size < 1024) {
        size = file.size + " B";
      } else if (file.size < 1024 * 1024) {
        size = +(file.size / 1024).toFixed(2) + " KB";
      } else {
        size = +(file.size / (1024 * 1024)).toFixed(2) + " MB";
      }

      // check file type
      fileType = getFileType(file);

      let requiredFileConfig = uploadFileSettings?.find(
        (x) => x.FileType == fileType
      );

      // check file size 10 MB
      if (file.size >= +requiredFileConfig?.AllowedMaxFileSize) {
        errorMessages.push(file.name);
      } else {
        // ==============================|| END FILE VALIDATION ||============================== //

        uploadedFileList = props.AllowMultipleSelect
          ? ([
              ...uploadedFileList,
              {
                Id: 0,
                File: file,
                FileName: filename,
                FilePath: URL.createObjectURL(file),
                FileType: fileType,
                FileSize: size,
                FileLength: file.size,
                UploadStatus: EnumDataTransferStatus.NotStarted,
                DownloadStatus: EnumDataTransferStatus.Default,
              } as UploadFileDTO,
            ] as Array<UploadFileDTO>)
          : ([
              {
                Id: 0,
                File: file,
                FileName: filename,
                FilePath: URL.createObjectURL(file),
                FileType: fileType,
                FileSize: size,
                FileLength: file.size,
                UploadStatus: EnumDataTransferStatus.NotStarted,
                DownloadStatus: EnumDataTransferStatus.Default,
              } as UploadFileDTO,
            ] as Array<UploadFileDTO>);
      }
    }

    // if any errors on file related to size shows here
    if (errorMessages && errorMessages.length > 0) {
      dispatch(
        openPopup({
          Open: true,
          BodyText: `
          The following file(s) are too large to upload. Maximum file size is ${getMaxAllowedSize()} per attachment.<br/>
         <ul class="upload-file-list">
          ${errorMessages
            .map((fileName: string, index: number) => {
              return `<li key={${index}}>${fileName}</li>`;
            })
            .join("")}</ul>`,
          HeaderText: "Warning",
          PopupType: EnumPopupType.WarningPopup,
        } as PopupProps)
      );
    }
    if (props.onChangeFileUploader) {
      props.onChangeFileUploader(uploadedFileList);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div style={{ padding: "15px 10px 15px 0 ", height: "100%" }}>
      <Box
        sx={{
          border: `1px dashed ${dragActive ? "#066fa4" : "#e0e0e0"}`,
          borderRadius: "8px",
          textAlign: "center",
          backgroundColor: dragActive ? "#f3f9ff" : "#fff",
          cursor: "pointer",
          transition: "all 0.3s ease",
          height: "160px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleFileDrop}
        onClick={handleUploadClick}
      >
        <DocumentUpload
          size="24"
          color={dragActive ? "#1976d2" : "#666"}
          style={{ marginBottom: "10px" }}
        />

        <MuiTypography
          variant="subtitle2"
          sx={{
            color: "#333",
            fontWeight: 500,
          }}
        >
          Click to choose file or drag and drop
        </MuiTypography>

        <MuiTypography
          variant="body2"
          sx={{
            color: "#666",
            marginBottom: "24px",
          }}
        >
          Excel files only - xlsx, xls (max <span>1 MB</span>)
        </MuiTypography>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".xlsx,.xls"
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />
      </Box>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20px",
        }}
      >
        <Button
          className="secondary-btn-small"
          variant="outlined"
          startIcon={<DocumentUpload variant="Outline" />}
          onClick={handleUploadClick}
          title="Add"
        >
          Upload
        </Button>
      </div>
    </div>
  );
};

export default HPRFileUploaderWithNavigation;
